import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

const useApi = (api, args = {}, deps = [], options = { isDisabled: false }) => {
  const [response, setResponse] = useState(null);
  const [error, setError] = useState();
  const [loading, setLoading] = useState(true);
  const isMounted = useRef(false);
  const lastCallId = useRef(0); // if component calls the api multiple times, discard previous & take the last response
  const [refetchRef, setRefetchRef] = useState(false);

  const refetch = () => {
    setRefetchRef(prev => !prev);
  };

  const callApi = useCallback(isCallDisabled => {
    const controller = new AbortController();
    const signal = { signal: controller.signal };
    if (isCallDisabled) {
      setLoading(false);
      return controller;
    }
    const callId = ++lastCallId.current;
    api({ ...args, ...signal })
      .then(res => {
        if (!isMounted.current || callId !== lastCallId.current) return;
        setResponse(
          options.respMapper ? options.respMapper(res.data) : res.data
        );
        setError();
      })
      .catch(err => {
        if (!isMounted.current || callId !== lastCallId.current) return;
        setResponse(null);
        setError(err);
      })
      .finally(() => {
        if (!isMounted.current || callId !== lastCallId.current) return;
        setLoading(false);
      });
    return controller;
  }, deps);

  useEffect(() => {
    isMounted.current = true;
    const controller = callApi(options.isDisabled);

    return () => {
      controller.abort();
      isMounted.current = false;
    };
  }, [callApi, options.isDisabled, refetchRef]);

  return [response, loading, error, refetch];
};

useApi.PropTypes = {
  api: PropTypes.func,
  responseMapper: PropTypes.func,
  args: PropTypes.shape({}),
  queryParams: PropTypes.objectOf(PropTypes.string),
  callDisabled: PropTypes.bool
};

export default useApi;
